import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useSession } from '../lib/sessionContext.jsx';
import { useTripJson } from '../hooks/data.js';
import { fetchPacking, togglePackingItem } from '../lib/packingApi.js';

const STORAGE_KEY_PREFIX = 'somesay_packing_local_'; // per-player local fallback

export default function Packing() {
  const { tripId } = useTrip();
  const session = useSession();
  const data = useTripJson(tripId, 'packing.json');
  const [checked, setChecked] = useState({});
  const [error, setError] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  const meId = session?.role === 'player' ? session.recipient_id : null;
  const localKey = meId ? `${STORAGE_KEY_PREFIX}${meId}` : null;

  // Hydrate: server first, fall back to localStorage if API unavailable
  // (dev / no-session). State syncs back to localStorage on every change
  // so refreshes don't lose work even before the player session lands.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!session.loading && meId) {
        try {
          const res = await fetchPacking({ tripId });
          if (!cancelled) {
            setChecked(res.checked || {});
            setHydrated(true);
            return;
          }
        } catch (err) {
          if (err.status !== 'api_unavailable' && err.status !== 401) {
            setError(err.message || String(err));
          }
        }
      }
      if (localKey && typeof window !== 'undefined') {
        const raw = window.localStorage.getItem(localKey);
        if (raw) {
          try {
            setChecked(JSON.parse(raw) || {});
          } catch {
            /* ignore */
          }
        }
      }
      if (!cancelled) setHydrated(true);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [tripId, meId, session.loading, localKey]);

  const onToggle = useCallback(
    async (itemId) => {
      const next = !checked[itemId];
      const optimistic = { ...checked };
      if (next) optimistic[itemId] = true;
      else delete optimistic[itemId];
      setChecked(optimistic);
      if (localKey && typeof window !== 'undefined') {
        window.localStorage.setItem(localKey, JSON.stringify(optimistic));
      }
      if (meId) {
        try {
          await togglePackingItem({ tripId, itemId, checked: next });
        } catch (err) {
          if (err.status !== 'api_unavailable') {
            setError(err.message || String(err));
          }
        }
      }
    },
    [checked, tripId, meId, localKey]
  );

  const rawSections = data.data?.sections;
  const sections = useMemo(() => rawSections || [], [rawSections]);

  const totals = useMemo(() => {
    let total = 0;
    let done = 0;
    for (const s of sections) {
      for (const it of s.items || []) {
        total += 1;
        if (checked[it.id]) done += 1;
      }
    }
    return { total, done };
  }, [sections, checked]);

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          per-player checklist · syncs to your phone
        </p>
        <h1 className="mt-1 font-serif text-2xl">Packing</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          {totals.done} of {totals.total} packed
          {totals.total > 0
            ? ` · ${Math.round((totals.done / totals.total) * 100)}% there`
            : ''}.
        </p>
      </header>

      {!session.loading && !meId && (
        <div className="card border-copper/40 bg-copper/5 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
            local-only mode
          </p>
          <p className="mt-1 text-sm text-fg-secondary">
            You&apos;re viewing the checklist without signing in. Check-marks save to this
            browser only — open from your briefing-email link to sync across devices.
          </p>
        </div>
      )}

      {data.loading && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          …loading checklist
        </p>
      )}

      {error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {sections.map((section) => (
          <SectionCard
            key={section.id}
            section={section}
            checked={checked}
            onToggle={onToggle}
            disabled={!hydrated}
          />
        ))}
      </div>

      {data.data?.notes && (
        <p className="px-1 text-xs italic text-fg-tertiary">{data.data.notes}</p>
      )}
    </div>
  );
}

function SectionCard({ section, checked, onToggle, disabled }) {
  const items = section.items || [];
  const done = items.filter((it) => checked[it.id]).length;
  return (
    <section className="card overflow-hidden">
      <header className="flex items-baseline justify-between gap-2 border-b border-border-tertiary bg-bg-secondary px-4 py-2">
        <h2 className="font-mono text-[11px] uppercase tracking-wider text-fg-secondary">
          {section.label}
        </h2>
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {done} / {items.length}
        </p>
      </header>
      <ul className="divide-y divide-border-tertiary">
        {items.map((it) => {
          const isChecked = !!checked[it.id];
          return (
            <li key={it.id}>
              <button
                type="button"
                onClick={() => onToggle(it.id)}
                disabled={disabled}
                className={[
                  'flex w-full items-center gap-3 px-4 py-2 text-left transition disabled:opacity-50',
                  isChecked ? 'bg-success/5' : 'hover:bg-bg-tertiary',
                ].join(' ')}
              >
                <span
                  aria-hidden
                  className={[
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded border font-mono text-xs',
                    isChecked
                      ? 'border-success bg-success/20 text-success'
                      : 'border-border-tertiary bg-bg-primary text-fg-tertiary',
                  ].join(' ')}
                >
                  {isChecked ? '✓' : ''}
                </span>
                <span
                  className={[
                    'flex-1 text-sm',
                    isChecked ? 'text-fg-tertiary line-through' : 'text-fg-primary',
                  ].join(' ')}
                >
                  {it.text}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
